import { useEffect, useMemo, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Mail, Loader2, Filter, ArrowRight, Package } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/*
 * Iter307 — Admin enquiry inbox.
 *
 * Owner-only page that lists every POA / £0 enquiry captured from the
 * basket flow. Supports status filtering (new / in_progress / closed),
 * inline status transitions, and a reply button that pre-fills a
 * mail draft (mailto:) with the enquirer's items.
 */
const STATUS_META = {
  new:         { label: "New",         accent: "#CCFF00" },
  in_progress: { label: "In progress", accent: "#66C2FF" },
  closed:      { label: "Closed",      accent: "#525252" },
};
const STATUSES = ["new", "in_progress", "closed"];

export default function AdminFestivalEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const ctl = useListControls(enquiries, { name: (e) => e.name, fields: (e) => [e.name, e.email, e.organisation, e.club_name], initialSort: "default" });
  const [filter, setFilter] = useState("new");
  const [loading, setLoading] = useState(true);

  const load = async (statusFilter) => {
    setLoading(true);
    try {
      const q = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const r = await api.get(`/admin/festival/enquiries${q}`);
      setEnquiries(r.data.enquiries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load enquiries");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(filter); }, [filter]);

  const counts = useMemo(() => {
    const c = { new: 0, in_progress: 0, closed: 0, all: enquiries.length };
    enquiries.forEach((e) => { if (STATUSES.includes(e.status)) c[e.status]++; });
    return c;
  }, [enquiries]);

  const transition = async (id, status) => {
    try {
      await api.patch(`/admin/festival/enquiries/${id}?status=${status}`);
      toast.success(`Marked ${STATUS_META[status].label}`);
      load(filter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const draftReply = (e) => {
    const itemsList = (e.items || []).map((it) => `• ${it.label || it.pass_type}`).join("\n");
    const subject = `Re: Rally-Lab festival pass enquiry`;
    const body = [
      `Hi ${e.name},`, "",
      `Thanks for your enquiry about the following items:`, itemsList || "(no items listed)", "",
      `Original message:\n${e.message || "(no message)"}`, "",
      "— Rally-Lab team",
    ].join("\n");
    const href = `mailto:${encodeURIComponent(e.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    // Auto-transition new → in_progress on first reply attempt.
    if (e.status === "new") transition(e.id, "in_progress");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-enquiries-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="label-mono text-[#CCFF00]">ADMIN · POA ENQUIRIES</div>
          <h1 className="h-display text-3xl mt-1">Festival pass enquiries</h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-lg">
            Every POA / £0 basket enquiry lands here. Reply within 24-72 hours to keep the promised SLA.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="admin-enquiry-filters">
        <Filter className="w-4 h-4 text-[#525252]" />
        {["all", ...STATUSES].map((s) => {
          const active = filter === s;
          const meta = STATUS_META[s] || { label: "All", accent: "#CCFF00" };
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`admin-enquiry-filter-${s}`}
              className="px-3 py-1.5 text-[10px] uppercase tracking-wider border"
              style={active
                ? { background: meta.accent, color: "#0A0A0A", borderColor: meta.accent, fontWeight: 700 }
                : { borderColor: "#262626", color: "#A3A3A3" }}
            >
              {meta.label}{s !== "all" && counts[s] > 0 && <span className="ml-1 opacity-70">({counts[s]})</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-sm text-[#525252] py-16 text-center inline-flex items-center gap-2 justify-center w-full">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : enquiries.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-[#525252]" data-testid="admin-enquiry-empty">
          No {STATUS_META[filter]?.label?.toLowerCase() || ""} enquiries.
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-enquiry-list">
          <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search enquiries…" testid="enquiries" />
          {ctl.list.map((e) => {
            const meta = STATUS_META[e.status] || STATUS_META.new;
            return (
              <div key={e.id} className="card-surface p-5" data-testid={`admin-enquiry-${e.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm text-white font-semibold">{e.name}</div>
                    <a href={`mailto:${e.email}`} className="text-xs text-[#66C2FF] hover:underline">{e.email}</a>
                    <div className="text-[10px] uppercase tracking-widest text-[#525252] mt-1">
                      {new Date(e.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[10px] uppercase tracking-widest px-2 py-1"
                      style={{ background: meta.accent, color: "#0A0A0A", fontWeight: 700 }}
                      data-testid={`admin-enquiry-${e.id}-status`}
                    >{meta.label}</span>
                    <button
                      type="button"
                      onClick={() => draftReply(e)}
                      data-testid={`admin-enquiry-${e.id}-reply`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wider bg-[#CCFF00] text-black font-bold hover:bg-[#B8E600]"
                    >
                      <Mail className="w-3 h-3" /> Reply
                    </button>
                  </div>
                </div>

                <div className="mt-3 grid gap-1">
                  {(e.items || []).length > 0 && (
                    <div className="border border-[#262626] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#525252] mb-2 inline-flex items-center gap-1">
                        <Package className="w-3 h-3" /> Items enquired
                      </div>
                      <ul className="text-xs text-white space-y-1">
                        {(e.items || []).map((it) => (
                          <li key={it.pass_type}>{it.label || it.pass_type} <span className="text-[#525252]">· {it.pass_type}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {e.message && (
                    <div className="border border-[#262626] p-3 text-sm text-[#E5E5E5] whitespace-pre-wrap">{e.message}</div>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-[#525252]">Move to</span>
                  {STATUSES.filter((s) => s !== e.status).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => transition(e.id, s)}
                      data-testid={`admin-enquiry-${e.id}-move-${s}`}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-white text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
                    >
                      {STATUS_META[s].label} <ArrowRight className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
